// Response envelope shared by every /api/v1 endpoint (migration-plan.md §3.1).
//
// The SPA branches on `error.code`, never on `error.message`. HTTP status still
// carries meaning; the code is the stable machine-readable contract.
/**
 * Stable error codes. Add to this list rather than inventing ad-hoc strings —
 * the client switches on these values.
 */
export const ErrorCodes = {
    // 400
    VALIDATION_FAILED: 'VALIDATION_FAILED',
    BAD_REQUEST: 'BAD_REQUEST',
    /** Role 999 (Owner) is reserved and can never be granted. */
    ROLE_RESERVED: 'ROLE_RESERVED',
    /** Manual close attempted on an `auto_at_end` type (Duration). */
    AUTO_CLOSE_ONLY: 'AUTO_CLOSE_ONLY',
    /** Winners submitted for a type whose `has_results` is false. */
    RESULTS_UNSUPPORTED: 'RESULTS_UNSUPPORTED',
    /** A submitted winner is not an active member of the event's group. */
    INVALID_WINNER: 'INVALID_WINNER',
    /** A `canceled` close cannot record winners. */
    CANCELED_NO_WINNERS: 'CANCELED_NO_WINNERS',
    /** Recurrence was submitted; series land in Phase 6. */
    RECURRENCE_UNSUPPORTED: 'RECURRENCE_UNSUPPORTED',
    /**
     * No file part in the multipart body. Deliberately distinct from
     * FILE_TOO_LARGE: a file rejected by a size limit arrives looking absent, and
     * reporting that as "choose a photo" is the misleading message the PHP
     * deployment shipped for months.
     */
    NO_FILE: 'NO_FILE',
    /** The file exceeded the upload cap. Its own code so the message can say so. */
    FILE_TOO_LARGE: 'FILE_TOO_LARGE',
    /** Content sniffing found something outside the image allowlist. */
    UNSUPPORTED_MEDIA: 'UNSUPPORTED_MEDIA',
    /** New password and its confirmation disagree. */
    PASSWORD_MISMATCH: 'PASSWORD_MISMATCH',
    /** New password is too short. Its own code so the form can point at one field. */
    PASSWORD_TOO_SHORT: 'PASSWORD_TOO_SHORT',
    /**
     * A destructive endpoint was called without its typed confirmation phrase.
     * Distinct from VALIDATION_FAILED so the form can highlight that one box
     * instead of reporting the whole request malformed.
     */
    CONFIRMATION_REQUIRED: 'CONFIRMATION_REQUIRED',
    /** The submitted phone number had no usable digits. */
    PHONE_INVALID: 'PHONE_INVALID',
    /**
     * Another account already holds that number. Phone is a login identifier, so
     * two accounts sharing one would make the identifier ambiguous.
     */
    PHONE_IN_USE: 'PHONE_IN_USE',
    /** Verification submitted with no pending number to verify. */
    PHONE_NOT_PENDING: 'PHONE_NOT_PENDING',
    /** The code was wrong. */
    PHONE_CODE_INVALID: 'PHONE_CODE_INVALID',
    /** The code was right but past its TTL. Distinct so the UI can offer a resend. */
    PHONE_CODE_EXPIRED: 'PHONE_CODE_EXPIRED',
    /**
     * Another account already holds that address. Email is a login identifier, so
     * two accounts sharing one would make the identifier ambiguous.
     *
     * ⚠ This DOES reveal that an address is registered, and that is deliberate:
     * the caller is already authenticated and changing their OWN identifier, which
     * is a different calculus from the forgot-password flow where every branch is
     * byte-identical. Mirrors PHONE_IN_USE. Without it a typo is undiagnosable.
     */
    EMAIL_IN_USE: 'EMAIL_IN_USE',
    /** The submitted address is already this account's address. */
    EMAIL_UNCHANGED: 'EMAIL_UNCHANGED',
    /** The change link was malformed, forged, for another purpose, or expired. */
    EMAIL_TOKEN_INVALID: 'EMAIL_TOKEN_INVALID',
    /** The multipart body could not be read, or the write failed. */
    UPLOAD_FAILED: 'UPLOAD_FAILED',
    // 401
    UNAUTHENTICATED: 'UNAUTHENTICATED',
    INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
    TOKEN_EXPIRED: 'TOKEN_EXPIRED',
    TOKEN_INVALID: 'TOKEN_INVALID',
    // 403
    FORBIDDEN: 'FORBIDDEN',
    ACCOUNT_INACTIVE: 'ACCOUNT_INACTIVE',
    ACCOUNT_LOCKED: 'ACCOUNT_LOCKED',
    ACCOUNT_SUSPENDED: 'ACCOUNT_SUSPENDED',
    /** The owner cannot be removed, demoted, or self-leave. */
    OWNER_PROTECTED: 'OWNER_PROTECTED',
    /** A group admin tried to touch a site-wide or another group's invite code. */
    CROSS_GROUP: 'CROSS_GROUP',
    /**
     * A masquerade is in read-only mode and this request would change something.
     * Its own code so the SPA can offer the write-mode switch instead of showing a
     * bare Forbidden on a page the impersonated user can plainly use.
     */
    MASQUERADE_READ_ONLY: 'MASQUERADE_READ_ONLY',
    /**
     * Refused in BOTH masquerade stages: credentials, and irreversible acts. Write
     * mode is permission to change data, never permission to take over an account
     * or to do something the subject cannot undo.
     */
    MASQUERADE_DENIED: 'MASQUERADE_DENIED',
    /**
     * The group is administratively suspended. Reads still work for its members;
     * every write is refused. Its own code so the SPA can say why rather than
     * showing a bare "Forbidden" on a group the user is plainly a member of.
     */
    GROUP_SUSPENDED: 'GROUP_SUSPENDED',
    /**
     * The group's category doesn't offer this event type. See docs/domain.md,
     * "Group categories" — a category only ever SUBSETS the registry.
     */
    EVENT_TYPE_NOT_ALLOWED: 'EVENT_TYPE_NOT_ALLOWED',
    // 404
    NOT_FOUND: 'NOT_FOUND',
    // 409
    CONFLICT: 'CONFLICT',
    ALREADY_MEMBER: 'ALREADY_MEMBER',
    CODE_EXISTS: 'CODE_EXISTS',
    /** The stamp-first close guard lost the race, or the event was already closed. */
    ALREADY_CLOSED: 'ALREADY_CLOSED',
    /** Write attempted against a closed event (RSVPs freeze on close). */
    EVENT_CLOSED: 'EVENT_CLOSED',
    /** A yes RSVP would exceed rsvp_max. */
    EVENT_FULL: 'EVENT_FULL',
    /**
     * Account deletion refused: the caller still owns a group with other members
     * in it. `details.groups` names them, so the SPA can link straight to each
     * one's Manage page rather than saying "some group somewhere".
     *
     * Its own code because it is the ONLY recoverable failure in the delete flow
     * and the recovery is a specific action -- transfer ownership -- not "try
     * again". See docs/plan-ios-ship.md item 1.
     */
    OWNS_GROUPS: 'OWNS_GROUPS',
    /** Day-of confirmation needs an existing yes RSVP. Covers "no row" too. */
    RSVP_NOT_YES: 'RSVP_NOT_YES',
    // 410
    GONE: 'GONE',
    /** Day-of confirmation arrived after the event's end instant. */
    EVENT_ENDED: 'EVENT_ENDED',
    // 429
    RATE_LIMITED: 'RATE_LIMITED',
    // 500
    INTERNAL: 'INTERNAL',
};
//# sourceMappingURL=envelope.js.map